import { directive, type ElementPart, PartType } from 'lit/directive.js'
import { AsyncDirective } from 'lit/async-directive.js'
import { animationFrameScheduler, EMPTY, fromEvent, merge, Subject } from 'rxjs'
import { auditTime, distinctUntilChanged, filter, ignoreElements, switchMap, takeUntil, tap } from 'rxjs/operators'
import { reducedMotion$ } from './reduced-motion'

export interface CursorGlowOptions {
	/** Glow radius in pixels (default: 200) */
	radius?: number
	/** Glow color — CSS color value (default: primary color) */
	color?: string
	/** Glow opacity 0-1 (default: 0.12) */
	intensity?: number
	/** Track viewport coordinates for a page that scrolls beneath the glow (default: false). */
	viewport?: boolean
}

class CursorGlowDirective extends AsyncDirective {
	private element!: HTMLElement
	private glowEl?: HTMLElement
	private lightEl?: HTMLElement
	private readonly teardown$ = new Subject<void>()
	private radius = 200
	private color = 'var(--schmancy-sys-color-primary-default)'
	private intensity = 0.12
	private viewport = false
	private prevKey?: string
	private didSetPosition = false
	private didReadPosition = false
	private pointer: PointerEvent | null = null

	render(options?: CursorGlowOptions) {
		void options
		return undefined
	}

	override update(part: ElementPart, [options]: [CursorGlowOptions?]) {
		if (part.type !== PartType.ELEMENT) {
			throw new Error('cursorGlow directive must be used on an element')
		}

		const key = JSON.stringify(options ?? {})
		if (this.element && key === this.prevKey) return undefined
		this.prevKey = key

		this.element = part.element as HTMLElement
		this.radius = options?.radius ?? 200
		this.color = options?.color ?? 'var(--schmancy-sys-color-primary-default)'
		this.intensity = options?.intensity ?? 0.12
		this.viewport = options?.viewport ?? false

		this.teardown$.next()
		// A host rendered while detached gets no disconnected() to end this subscription.
		if (this.isConnected) this.setupTracking()

		return undefined
	}

	override reconnected() {
		this.teardown$.next()
		this.setupTracking()
	}

	private setupTracking() {
		reducedMotion$
			.pipe(
				distinctUntilChanged(),
				switchMap(reduced => {
					if (reduced) {
						this.goDark()
						return EMPTY
					}

					if (!this.glowEl || !this.lightEl) {
						this.glowEl = document.createElement('div')
						this.glowEl.setAttribute('aria-hidden', 'true')
						Object.assign(this.glowEl.style, {
							inset: '0',
							pointerEvents: 'none',
							zIndex: '0',
							borderRadius: 'inherit',
							overflow: 'hidden',
							opacity: '0',
							transition: 'opacity 400ms ease',
						})
						this.lightEl = document.createElement('div')
						this.lightEl.style.position = 'absolute'
						this.glowEl.append(this.lightEl)
						this.element.prepend(this.glowEl)
					}
					this.glowEl.style.position = this.viewport ? 'fixed' : 'absolute'
					// The light is painted once and moved by transform: repainting a gradient
					// that spans the layer on every move redraws the whole layer each frame.
					Object.assign(this.lightEl.style, {
						width: `${this.radius * 2}px`,
						height: `${this.radius * 2}px`,
						background: `radial-gradient(circle closest-side, color-mix(in srgb, ${this.color} ${Math.round(this.intensity * 100)}%, transparent), transparent)`,
					})

					// Options changing mid-hover rebuild this: the light stays on the pointer.
					this.placeLight()

					// Touch synthesizes mouseenter and mousemove and never a mouseleave, and
					// only a pointer event says which input produced it.
					return merge(
						merge(
							fromEvent<PointerEvent>(this.element, 'pointerenter'),
							fromEvent<PointerEvent>(this.element, 'pointermove'),
						).pipe(
							filter(e => e.pointerType !== 'touch'),
							tap(e => {
								this.pointer = e
							}),
							auditTime(0, animationFrameScheduler),
						),
						fromEvent<PointerEvent>(this.element, 'pointerleave').pipe(
							filter(e => e.pointerType !== 'touch'),
							tap(() => this.goDark()),
							ignoreElements(),
						),
					)
				}),
				takeUntil(this.teardown$),
			)
			.subscribe(() => this.placeLight())
	}

	private goDark() {
		this.pointer = null
		if (this.glowEl) this.glowEl.style.opacity = '0'
		if (this.lightEl) this.lightEl.style.willChange = ''
	}

	private placeLight() {
		if (!this.pointer || !this.glowEl || !this.lightEl) return
		// Read here because lit commits a directive before it inserts the element,
		// where computed style answers nothing.
		if (!this.viewport && !this.didReadPosition) {
			this.didReadPosition = true
			if (getComputedStyle(this.element).position === 'static') {
				this.element.style.position = 'relative'
				this.didSetPosition = true
			}
		}
		let left = 0
		let top = 0
		if (!this.viewport) {
			// The layer's inset anchors to the padding box, inside any border.
			const box = this.element.getBoundingClientRect()
			left = box.left + this.element.clientLeft
			top = box.top + this.element.clientTop
		}
		this.glowEl.style.opacity = '1'
		this.lightEl.style.willChange = 'transform'
		this.lightEl.style.transform = `translate(${this.pointer.clientX - left - this.radius}px, ${this.pointer.clientY - top - this.radius}px)`
	}

	override disconnected() {
		this.teardown$.next()
		this.glowEl?.remove()
		this.glowEl = undefined
		this.lightEl = undefined
		this.pointer = null
		this.didReadPosition = false
		if (this.didSetPosition && this.element) {
			this.element.style.position = ''
			this.didSetPosition = false
		}
	}
}

export const cursorGlow = directive(CursorGlowDirective)
